import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { authFetch } from '../../../../../../../CommonUtils/authFetch';
import { httpBodyOptions } from '../../../../../../../CommonUtils/httpBodyOptions';
import { urlWithParams } from '../../../../../../../CommonUtils/urlWithParams';
import { setPlanImpact } from '../../../../../../../FinancialPlan/FinancialPlanActions';
import { setWhatIfsBody } from '../../../../../../../WhatIfs/WhatIfsActions';
import { RetirementExpenses } from '../../../../../../../ProfileOPL/Goals/goals/Retirement/Retirement.meta';
import {
  getMonteCarloQueryData,
  invalidateFinancialPlanQuery,
  invalidateMonteCarloQuery,
  monteCarloQueryDataRequest,
  updateFinancialPlanQueryData,
} from '../../../../../helpers/legacyWhatIfsFinancialPlanHelpers';

interface Params {
  clientId: number;
  fpId: string;
  goalId?: string;
  percentOfLivingExpenses: RetirementExpenses;
  /**
   * Absolute yearly target for the selected option. Required because the Monte Carlo and
   * Percentiles endpoints deserialize the what-if directly and never run the server-side
   * percent-to-amount resolution that PlanImpact/ApplyToPlan get from OnWhatIfSubmitted.
   */
  resolvedLivingExpenseAmount: number | null;
}

const PLAN_IMPACT_DEBOUNCE_MS = 400;

// Every previewed endpoint needs the absolute yearly target: Monte Carlo and Percentiles because they
// never run the server-side percent-to-amount resolution, PlanImpact because it has to agree with them.
// So a percent whose amount could not be resolved is as unpreviewable as an empty custom amount, even
// though PlanImpact alone would have resolved it server-side and looked fine.
const isValid = (resolvedAmount: number | null): boolean =>
  resolvedAmount != null && resolvedAmount > 0;

/**
 * Previews the plan impact of the living-expenses what-if as inputs change, refreshing the goal
 * charts (financial plan goals and, when open, Monte Carlo) without requiring the user to apply.
 * Mirrors the legacy plan-impact-on-change behavior of the other what-ifs.
 */
export const useLivingExpensesPlanImpactPreview = ({
  clientId,
  fpId,
  goalId,
  percentOfLivingExpenses,
  resolvedLivingExpenseAmount,
}: Params) => {
  const dispatch = useDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasAppliedPreviewRef = useRef(false);

  const restorePersistedPlan = useCallback(() => {
    dispatch(setWhatIfsBody(null));
    invalidateFinancialPlanQuery({ clientId, fpId });
    invalidateMonteCarloQuery({ clientId, fpId });

    // Invalidating react-query does not touch the legacy store, which keeps whatever was last
    // pushed through setPlanImpact. Refetching the plan is how the legacy what-if footer discards
    // a preview on cancel, and it is the only way to get the goal charts off this preview.
    if (hasAppliedPreviewRef.current) {
      hasAppliedPreviewRef.current = false;
      ADV.NewFinancialPlan.refreshPlan(true);
    }
  }, [clientId, fpId, dispatch]);

  const requestPlanImpact = useMemo(
    () =>
      debounce(async (percent: RetirementExpenses, resolvedAmount: number | null) => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const whatIf = {
          WhatIfKey: '1800',
          WhatIfType: 1800,
          WhatIfTypeName: 'LivingExpensesChange',
          PercentOfLivingExpenses: percent,
          LivingExpenseAmount: resolvedAmount,
        };

        try {
          const viewData = await authFetch(
            urlWithParams('/Consumer/WhatIfLivingExpensesChange/PlanImpact', {
              clientId,
              fpId,
              goalId,
            }),
            {
              method: 'POST',
              signal: controller.signal,
              ...httpBodyOptions(whatIf, true),
            },
          );

          // A newer input change superseded this request while it was in flight, so its response is
          // stale and must not be written to the charts.
          if (controller.signal.aborted) {
            return;
          }

          // Drives the what-if-aware charts (e.g. Market Scenarios) that read the staged what-ifs.
          dispatch(setWhatIfsBody([whatIf]));

          const { financialPlan } = viewData;
          if (financialPlan?.Goals) {
            updateFinancialPlanQueryData({ clientId, fpId, financialPlan });

            // The goal charts rendered in the what-if shell are driven by the legacy Redux store,
            // not the react-query cache, so the plan impact has to be pushed there too or nothing
            // redraws.
            dispatch(setPlanImpact(clientId, financialPlan));
            hasAppliedPreviewRef.current = true;
          }

          // Only re-run Monte Carlo when its chart is currently loaded, to avoid a costly
          // simulation request when the user is not looking at it. Kept after the goal charts so a
          // Monte Carlo failure cannot discard an otherwise good plan impact.
          const monteCarloData = getMonteCarloQueryData({ clientId, fpId });
          if ('Data' in monteCarloData && monteCarloData.Data) {
            try {
              await monteCarloQueryDataRequest({ clientId, fpId, body: [whatIf] });
            } catch (monteCarloError) {
              // Leave the goal charts on this preview and let Monte Carlo fall back to the
              // persisted plan; a superseded request (AbortError) needs no handling at all.
              if (!(monteCarloError instanceof DOMException)) {
                invalidateMonteCarloQuery({ clientId, fpId });
              }
            }
          }
        } catch (error) {
          // Superseded requests reject with an AbortError, which is expected. For any other
          // failure, restore the persisted plan so the charts do not show a broken preview.
          if (!(error instanceof DOMException)) {
            restorePersistedPlan();
          }
        }
      }, PLAN_IMPACT_DEBOUNCE_MS),
    [clientId, fpId, goalId, dispatch, restorePersistedPlan],
  );

  const hasMountedRef = useRef(false);

  useEffect(() => {
    // Skip the initial render: charts already reflect the persisted plan on mount.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (!isValid(resolvedLivingExpenseAmount)) {
      // No usable yearly target (e.g. "Other" with no amount, or an option whose amount is missing):
      // cancel any pending/in-flight preview and restore the persisted plan so the charts do not show
      // a stale preview.
      requestPlanImpact.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      restorePersistedPlan();
      return;
    }

    requestPlanImpact(percentOfLivingExpenses, resolvedLivingExpenseAmount);
  }, [
    percentOfLivingExpenses,
    resolvedLivingExpenseAmount,
    requestPlanImpact,
    restorePersistedPlan,
  ]);

  useEffect(
    () => () => {
      requestPlanImpact.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Runs after a successful apply too, which is redundant on the retrofit shell (its onSave
      // refreshes the same things) but is the only plan refresh on the OPL shell, whose onSuccess
      // only toasts and closes. Every step here is also correct once the what-if is persisted: the
      // refetches return the applied plan, and nothing is staged anymore.
      restorePersistedPlan();
    },
    [requestPlanImpact, restorePersistedPlan],
  );
};
